'use client';
import { Dialog, Transition } from '@headlessui/react';
import Image from 'next/image';
import { Fragment, useCallback, useEffect, useState } from 'react';
import CloseCart from './close-cart';
import OpenCart from './open-cart';
import { ShoppingCartIcon } from 'lucide-react';
import Price from '../price';
import { CartState, useCartStore } from '../useCartStore';
import EditItemQuantityButton from './EditQuantity';
import DeleteItemButton from './DeleteItem';
import { loadEcwidScript } from '../checkout/utils';
import { Cart } from '@/lib/ecwid/ecwidTypes';

export default function CartModal({
    locale,
    translation,
}: {
    locale: string;
    translation: { [key: string]: string };
}) {
    const { cart, setCart, cartOpen, setCartOpen } = useCartStore(
        (state: CartState) => ({
            cart: state.cart,
            setCart: state.setCart,
            cartOpen: state.cartOpen,
            setCartOpen: state.setCartOpen,
        }),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [disableAnimation, setDisableAnimation] = useState(true);
    const [ecwidLoaded, setEcwidLoaded] = useState(false);

    useEffect(() => {
        loadEcwidScript(process.env.NEXT_PUBLIC_ECWID_STORE_ID, () => {
            setEcwidLoaded(true);
        });
    }, []);

    useEffect(() => {
        setDisableAnimation(false);
    }, []);

    const stableSetCart = useCallback(
        (cart: Cart) => {
            setCart(cart);
        },
        [setCart],
    );

    useEffect(() => {
        if (typeof window.Ecwid !== 'undefined' && ecwidLoaded) {
            window.Ecwid.OnCartChanged.add(stableSetCart);

            return () => {
                if (typeof window.Ecwid.OnCartChanged.remove === 'function') {
                    window.Ecwid.OnCartChanged.remove(stableSetCart);
                }
            };
        }
    }, [stableSetCart, ecwidLoaded]);

    const openCart = () => {
        setCartOpen(true);
    };

    const closeCart = () => {
        setCartOpen(false);
        setIsOpen(false);
    };

    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    if (!ecwidLoaded) {
        return null;
    }

    return (
        <>
            <button aria-label="Open cart" onClick={openCart}>
                <OpenCart
                    quantity={
                        cart?.items?.reduce(
                            (total, item) => total + item.quantity,
                            0,
                        ) || 0
                    }
                />
            </button>
            <Transition show={cartOpen}>
                <Dialog
                    className="relative z-50"
                    onClose={closeCart}
                    open={isOpen}
                    static
                >
                    <Transition.Child
                        as={Fragment}
                        enter={
                            !disableAnimation
                                ? 'transition-all ease-in-out duration-300'
                                : ''
                        }
                        enterFrom={!disableAnimation ? 'translate-x-full' : ''}
                        enterTo={!disableAnimation ? 'translate-x-0' : ''}
                        leave={
                            !disableAnimation
                                ? 'transition-all ease-in-out duration-200'
                                : ''
                        }
                        leaveFrom={!disableAnimation ? 'translate-x-0' : ''}
                        leaveTo={!disableAnimation ? 'translate-x-full' : ''}
                    >
                        <Dialog.Panel
                            className="fixed bottom-0 right-0 top-0 flex h-full w-full flex-col border-l border-neutral-200 bg-white/70 p-6 text-black backdrop-blur-xl dark:border-neutral-700 dark:bg-black/80 dark:text-white md:w-[480px]"
                            onClick={handleModalClick}
                        >
                            <div className="flex items-center justify-between">
                                <p className="text-lg font-semibold">
                                    {translation?.title[locale]}
                                </p>
                                <button
                                    aria-label="Close cart"
                                    onClick={closeCart}
                                >
                                    <CloseCart />
                                </button>
                            </div>

                            {!cart || cart?.items?.length === 0 ? (
                                <div className="mt-20 flex w-full flex-col items-center justify-center overflow-hidden">
                                    <ShoppingCartIcon className="h-16" />
                                    <p className="mt-6 text-center text-2xl font-bold">
                                        {translation?.emptyCart[locale]}
                                    </p>
                                </div>
                            ) : (
                                <div className="flex h-full flex-col justify-between overflow-hidden p-1">
                                    <ul className="flex-grow overflow-auto py-4">
                                        {cart?.items?.map((item, i) => {
                                            return (
                                                <li
                                                    className="flex w-full flex-col border-b border-neutral-300 dark:border-neutral-700"
                                                    key={i}
                                                >
                                                    <div className="relative flex w-full flex-row justify-between px-1 py-4">
                                                        <div className="absolute z-40 -mt-2 ml-[55px]">
                                                            <DeleteItemButton
                                                                index={i}
                                                                item={item}
                                                                translation={
                                                                    translation
                                                                }
                                                            />
                                                        </div>
                                                        <div className="z-30 flex flex-row space-x-4">
                                                            <div className="relative h-16 w-16 cursor-pointer overflow-hidden rounded-md border border-neutral-300 bg-neutral-300 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800">
                                                                <Image
                                                                    alt={
                                                                        item
                                                                            .product
                                                                            .name
                                                                    }
                                                                    className="h-full w-full object-cover"
                                                                    height={64}
                                                                    src={
                                                                        item
                                                                            .product
                                                                            .mediaItem
                                                                            ?.image400pxUrl
                                                                    }
                                                                    width={64}
                                                                />
                                                            </div>

                                                            <div className="flex flex-1 flex-col text-base">
                                                                <span className="leading-tight">
                                                                    {
                                                                        item
                                                                            .product
                                                                            .name
                                                                    }
                                                                </span>
                                                                <span className="mt-2 text-sm text-neutral-700">
                                                                    {item.options &&
                                                                        Object.entries(
                                                                            item.options,
                                                                        )
                                                                            .map(
                                                                                ([
                                                                                    key,
                                                                                    value,
                                                                                ]) =>
                                                                                    `${key}: ${value}`,
                                                                            )
                                                                            .join(
                                                                                ', ',
                                                                            )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="flex h-16 flex-col justify-between">
                                                            <Price
                                                                amount={
                                                                    item.product
                                                                        .price
                                                                }
                                                                className="flex justify-end space-y-2 text-right text-sm"
                                                                currencyCode="CAD"
                                                            />
                                                            <div className="mx-auto flex h-9 flex-row items-center">
                                                                <EditItemQuantityButton
                                                                    index={i}
                                                                    item={item}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    <div className="py-4">
                                        <a
                                            className="block w-full rounded-lg bg-primary px-4 py-2 text-center text-white hover:bg-primary-dark"
                                            href={'/checkout?store-page=cart'}
                                        >
                                            {translation?.shopping ||
                                                'Passer à la caisse'}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </Dialog>
            </Transition>
        </>
    );
}
